<script>
    import { fly } from "svelte/transition";
    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { ServiceConsts } from "$/services/ServiceConsts";
    import { tooltip } from "$/utils/tooltip";

    export let topic;
    export let showShareButton = true;
    export let showDashboardButton = true;
    export let shareButtonClicked;
    export let dashboardButtonClicked = () => {
        window.open(
            ServiceConsts.DASHBOARD_URL +
                "/#/" +
                $ActiveTopic?.tenantId +
                "/t/" +
                $ActiveTopic?.id,
            "_blank",
        );
    };

    let shareLink;
    $: {
        if (!topic) {
            shareLink = "Loading...";
        } else if (topic?.id?.length > 30) {
            shareLink = ServiceConsts.LOAD_URL + topic.id;

            // if topic shared from firebase
        } else if (topic?.id?.length < 30) {
            shareLink = `${ServiceConsts.LOAD_URL_LEGACY}?${ServiceConsts.LOAD_URL_LEGACY_PARAM}=${topic.id}`;
        }
    }

    $: showButtons = topic?.id?.length > 30;

    let shareLinkInput;
    let shareLinkToast;

    const copyLink = async () => {
        shareLinkInput.select();
        /* 
            Use navigator.clipboard.writeText instead of document.execCommand("copy")
            because execCommand is deprecated and has inconsistent browser support.
            Clipboard api (navigator.clipboard) is more secure and reliable
            navigator.clipboard.writeText works only in secure contexts (HTTPS or localhost) 
            works as async and avoids need to manually select the input elements
            but we will select the input to show the user that the link is copied
        */ 
        await navigator.clipboard.writeText(shareLinkInput.value);
        shareLinkToast = "Copied to clipboard";
        setTimeout(() => {
            shareLinkToast = null;
        }, 2000);
    };
</script>

{#if shareLinkToast}
    <div class="share-link-toast" transition:fly>
        {shareLinkToast}
    </div>
{/if}

<div class="share-link-input-wrapper">
    {#if $ActiveTopic && ActiveTopicStore.isInbox()}
        <a
            class="share-link-input"
            href={ServiceConsts.DASHBOARD_URL +
                "/#/" +
                $TenantStore?.active.id +
                "/inbox"}
            target="_blank"
            rel="noopener noreferrer"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                />
            </svg>
            <span> Open in dashboard </span>
        </a>
    {:else}
        <input
            class="share-link-input"
            value={shareLink}
            readonly
            on:click={copyLink}
            bind:this={shareLinkInput}
            use:tooltip={shareLink !== "Loading..." ? "Copy to clipboard" : ""}
        />
    {/if}

    {#if showButtons}
        <div class="share-buttons">
            {#if showShareButton}
                <button
                    class="share-button"
                    on:click={shareButtonClicked}
                    use:tooltip={"Collaborators"}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                        />
                    </svg>
                </button>
            {/if}

            {#if showDashboardButton}
                <button
                    class="share-button"
                    on:click={dashboardButtonClicked}
                    use:tooltip={"Open dashboard"}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                        />
                    </svg>
                </button>
            {/if}
        </div>
    {/if}
</div>

<style>
    @import "../main.css";

    .share-link-toast {
        @apply reset absolute -top-6 left-0 w-full bg-black px-4 py-2 text-sm text-white !important;
    }

    .share-link-input-wrapper {
        @apply relative w-full !important;
    }

    .share-link-input {
        @apply reset flex w-full cursor-pointer gap-2 rounded-full border border-solid border-gray-800 bg-white px-4 py-2 no-underline !important;
    }

    .share-buttons {
        @apply reset absolute right-1 top-1 flex flex-row rounded-full bg-white shadow-[-10px_0px_10px_0px_rgba(255,255,255,1)] !important;
    }

    .share-button {
        @apply reset rounded-full p-2 leading-none hover:bg-gray-200 !important;
    }

    .share-button svg {
        @apply reset h-4 w-4 !important;
    }
</style>
